import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { FileTypeIcon } from "components/Document/shared/fileTypeUtils";
import { extensionFromName } from "components/Document/shared/util/extensionFromName";
import { Dropzone } from "components/Dropzone";
import { DisplayOnlyField } from "components/Forms/DisplayOnlyField";
import { DocumentType, Permission } from "generated-graphql/graphql";
import { useDeleteDocumentMutation } from "hooks/documents";
import { useAuthorization } from "hooks/useAuthorization";
import { useDocumentUpload } from "hooks/useDocumentUpload";
import { useTenant } from "hooks/useTenant";
import { useCallback, useState } from "react";

import {
  GET_WASTE_STREAM_PROFILE,
  useAttachWasteProfileDocument,
  useGenerateWasteProfileSummary,
} from "./api";
import { AutoFillProfileDialog } from "./AutoFillProfileDialog";
import { prettyPrintMissingField } from "./profileDisplay";
import { ProfileSection } from "./ProfileSection";
import { WasteProfileSectionData, WasteStreamProfileData } from "./types";

const FIELD = { xs: 12, sm: 6, lg: 3 } as const;

const orBlank = (value?: string | null): string | undefined => value || undefined;

/**
 * Whether the profile holds any characterization a summary could describe.
 *
 * `completeness` cannot answer this: it only asks about the profile document and
 * specific gravity, so an otherwise empty profile reads as complete. Generating
 * from nothing produces an empty sheet and replaces whatever document is in the
 * slot, which is why the control is refused rather than left to disappoint.
 *
 * Read against what the generator actually renders, not against the
 * characterization alone — it opens with the profile number, TSDF and shipping
 * name, so a profile holding only those has plenty to summarize.
 */
const hasCharacterization = (
  profile: WasteProfileSectionData | null,
  wasteStream: WasteStreamProfileData
): boolean => {
  if (!profile) return false;
  return (
    // The identity and DOT blocks are what the generated summary leads with, so
    // a profile carrying only those still has a document worth producing.
    !!profile.profileNumber ||
    !!profile.tsdfName ||
    !!profile.tsdfProfileId ||
    !!wasteStream.dotProperShippingName ||
    !!wasteStream.dotIdNumber ||
    !!wasteStream.dotHazardClass ||
    !!wasteStream.dotSubsidiaryHazardClass ||
    wasteStream.dotPackingGroup != null ||
    wasteStream.dotHazardous ||
    profile.physicalState != null ||
    profile.phLow != null ||
    profile.phHigh != null ||
    profile.flashPointF != null ||
    // A profile whose only flash-point answer is a range or a "does not flash"
    // has still been characterized, and is worth a generated summary.
    profile.flashPointMinF != null ||
    profile.flashPointMaxF != null ||
    profile.flashPointQualifier != null ||
    profile.specificGravity != null ||
    profile.isIgnitable ||
    profile.isCorrosive ||
    profile.isReactive ||
    profile.isToxic ||
    !!profile.physicalDescription ||
    !!profile.generatingProcess ||
    // The generated summary renders these, so a profile carrying only the codes
    // read off a sheet — which is a real characterization — must not report as
    // empty and disable the button that would render them.
    profile.federalWasteCodes.length > 0 ||
    profile.stateWasteCodes.length > 0 ||
    profile.constituents.length > 0
  );
};

const formatUpdatedAt = (value?: string | null): string | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString();
};

/**
 * What the profile is still missing, and nothing when it is missing nothing.
 *
 * There is deliberately no "complete" state. The server derives completeness
 * from two things — a profile document, and a specific gravity when the stream
 * has a volumetric container — so it goes green on a characterization that is
 * otherwise empty. Naming a gap is useful; announcing completeness on that basis
 * tells the reader something we have not checked.
 */
const CompletenessLine = ({
  completeness,
}: {
  completeness?: { complete: boolean; missing: string[] } | null;
}) => {
  if (!completeness || completeness.missing.length === 0) return null;

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <ErrorOutline sx={{ fontSize: "1.125rem", color: "warning.main", flexShrink: 0 }} />
      <Typography variant="body2" color="text.secondary">
        Still needed: {completeness.missing.map(prettyPrintMissingField).join(", ")}
      </Typography>
    </Stack>
  );
};

/**
 * The document going up, named because a replace leaves the old one on screen
 * until the new one lands. Same leading-icon idiom as `CompletenessLine`.
 */
const UploadingLine = ({ fileName, attaching }: { fileName: string; attaching: boolean }) => (
  <Stack direction="row" spacing={1} alignItems="center" aria-live="polite">
    <CircularProgress size={16} sx={{ flexShrink: 0 }} />
    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
      {attaching ? `Attaching ${fileName}…` : `Uploading ${fileName}…`}
    </Typography>
  </Stack>
);

export const ProfileDocumentSection = ({
  wasteStream,
  isReadOnly,
}: {
  wasteStream: WasteStreamProfileData;
  isReadOnly: boolean;
}) => {
  const profile = wasteStream.profile ?? null;
  const document = profile?.profileDocument ?? null;
  const alerts = useAlerts();
  const { tenantId } = useTenant();
  const { hasPermissions, hasPermissionForFacility } = useAuthorization();

  const canDeleteDocument =
    !isReadOnly &&
    (wasteStream.facilityId
      ? hasPermissionForFacility(wasteStream.facilityId, [Permission.WriteDocument])
      : hasPermissions([Permission.WriteDocument]));

  const [attachDocument, { loading: attaching }] = useAttachWasteProfileDocument();

  const [deleteDocument, { loading: deleting }] = useDeleteDocumentMutation({
    refetchQueries: [{ query: GET_WASTE_STREAM_PROFILE, variables: { id: wasteStream.id } }],
    awaitRefetchQueries: true,
  });

  const [generateSummary, { loading: generating }] = useGenerateWasteProfileSummary();
  const [confirmReplaceOpen, setConfirmReplaceOpen] = useState(false);
  const [autoFillOpen, setAutoFillOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<typeof document>(null);
  // Null whenever nothing is in flight, which is what shows the progress row.
  const [uploadingFileName, setUploadingFileName] = useState<string | null>(null);

  /**
   * Upload and attach are two steps by design: `upsertDocument` creates the
   * Document row and the S3 object, then `attachWasteProfileDocument` points
   * the profile at it and flips the document type. The hook reports the
   * created row through `onSuccess`, so the attach is wired there.
   */
  const { uploadFile, isUploading } = useDocumentUpload({
    onError: (error) => {
      setUploadingFileName(null);
      alerts.error("Failed to upload profile document", error.message);
    },
    onSuccess: async (uploaded) => {
      if (!uploaded) {
        setUploadingFileName(null);
        return;
      }
      try {
        await attachDocument({
          variables: { wasteStreamId: wasteStream.id, documentId: uploaded.id },
        });
        alerts.success("Profile document attached");
      } catch (error) {
        alerts.error(
          "Failed to attach profile document",
          error instanceof Error ? error.message : undefined
        );
        // The upload already created the Document row and the S3 object, and
        // nothing points at them now. Without this the user retries and every
        // failed attempt leaves another document behind in their Documents
        // list. A failed cleanup is not worth a second error toast on top of
        // the one they already have.
        try {
          await deleteDocument({ variables: { id: uploaded.id } });
        } catch {
          // Intentionally ignored, see above.
        }
      } finally {
        setUploadingFileName(null);
      }
    },
  });

  // `attaching` has to be in here alongside the upload: `useDocumentUpload`
  // calls `onSuccess` without awaiting it, so `isUploading` goes false the
  // moment the Document row exists, while the attach that points the profile
  // at it is still in flight. Without this, the dropzone and Generate summary
  // re-enable mid-attach, and every one of those actions writes the same
  // single profile-document slot — the winner would be whichever request
  // finished last, not the action the user took last.
  const busy = isUploading || attaching || generating || deleting;

  const runDelete = async () => {
    if (!pendingDelete || busy || !canDeleteDocument) return;

    try {
      await deleteDocument({ variables: { id: pendingDelete.id } });
      setPendingDelete(null);
      setAutoFillOpen(false);
      alerts.success("Profile document deleted");
    } catch (error) {
      alerts.error(
        "Failed to delete profile document",
        error instanceof Error ? error.message : undefined
      );
    }
  };

  const onDrop = useCallback(
    (files: File[]) => {
      const file = files[0];
      if (!file) return;
      if (!tenantId) {
        alerts.error("Cannot upload without a tenant");
        return;
      }
      setUploadingFileName(file.name);
      void uploadFile({
        file,
        currentSubmissionDocument: false,
        documentData: {
          tenantId,
          title: file.name,
          fileExtension: extensionFromName(file.name),
          documentType: DocumentType.WasteStreamProfile,
        },
      });
    },
    [alerts, tenantId, uploadFile]
  );

  const runGenerate = useCallback(async () => {
    setConfirmReplaceOpen(false);
    try {
      await generateSummary({ variables: { wasteStreamId: wasteStream.id } });
      alerts.success("Profile summary generated");
    } catch (error) {
      alerts.error(
        "Failed to generate profile summary",
        error instanceof Error ? error.message : undefined
      );
    }
  }, [alerts, generateSummary, wasteStream.id]);

  /**
   * Attach and generate write the same single slot, last write wins, so
   * generating over an existing sheet is confirmed first: the replaced row
   * survives in the database but nothing in this UI can reach it again.
   */
  const handleGenerateClick = () => {
    if (document) {
      setConfirmReplaceOpen(true);
      return;
    }
    void runGenerate();
  };

  const updatedAt = formatUpdatedAt(document?.updatedAt);
  const summarizable = hasCharacterization(profile, wasteStream);
  const applied = profile?.extractionApplied ?? false;

  return (
    <ProfileSection
      title="Profile & document"
      action={
        // Neutral rather than coloured: this renders our own data back as a
        // document, which is a utility beside the characterization work the
        // section is actually for.
        <Tooltip
          title={
            summarizable ? "" : "Add a characterization first — there is nothing to summarize yet."
          }
        >
          <span>
            <Button
              size="small"
              sx={{ color: "text.secondary" }}
              startIcon={generating ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
              onClick={handleGenerateClick}
              disabled={busy || isReadOnly || !summarizable}
            >
              Generate summary
            </Button>
          </span>
        </Tooltip>
      }
    >
      <Stack spacing={2}>
        <Grid container spacing={2} sx={{ alignItems: "flex-start" }}>
          <Grid size={FIELD}>
            <DisplayOnlyField
              label="Profile number"
              text={orBlank(profile?.profileNumber)}
              copyable
            />
          </Grid>
          <Grid size={FIELD}>
            <DisplayOnlyField label="TSDF" text={orBlank(profile?.tsdfName)} copyable />
          </Grid>
          <Grid size={FIELD}>
            <DisplayOnlyField
              label="TSDF profile ID"
              text={orBlank(profile?.tsdfProfileId)}
              copyable
            />
          </Grid>
        </Grid>

        <CompletenessLine completeness={profile?.completeness} />

        {document ? (
          // No border or surface of its own: the section card is already a
          // surface, and the drawer's other in-card rows are plain.
          <Stack direction="row" spacing={1} alignItems="center">
            <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
              {/* The icon rides with the title rather than being centred on the
                  two-line block, which is how the Documents and facility-document
                  grids draw a document row. */}
              <Stack direction="row" spacing={0.75} alignItems="center">
                <FileTypeIcon ext={document.fileExtension} />
                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                  {document.title}
                </Typography>
                {/* Beside the title rather than out with the actions: it acts on
                    this document specifically, and as an icon it stops competing
                    with the one action the section is for. */}
                {document.downloadLink && (
                  <Tooltip title="Download">
                    <IconButton
                      size="small"
                      aria-label={`Download ${document.title ?? "profile document"}`}
                      component={Link}
                      href={document.downloadLink}
                      target="_blank"
                      rel="noopener"
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {canDeleteDocument && (
                  <Tooltip title="Delete profile document">
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Delete profile document"
                        onClick={() => setPendingDelete(document)}
                        disabled={busy}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Stack>
              {updatedAt && (
                <Typography variant="caption" color="text.secondary">
                  Last updated {updatedAt}
                </Typography>
              )}
            </Stack>
            {/* Only an uploaded sheet is offered: a generated summary is our own
                render of the columns this would fill in, so reading it back
                would describe our output to us — and the mutation rejects it. */}
            {document.documentType === DocumentType.WasteStreamProfile && (
              // Only offers itself while there is still an auto-fill worth
              // doing. Once the reading has been applied its values are on the
              // profile, so it drops the sparkle and the colour and sits with
              // the other utilities — the AI work is done, not on offer.
              <Button
                size="small"
                sx={applied ? { color: "text.secondary" } : undefined}
                startIcon={applied ? undefined : <AutoAwesomeIcon />}
                onClick={() => setAutoFillOpen(true)}
                disabled={busy || isReadOnly}
              >
                {applied ? "Review auto-fill" : "Auto-fill from this document"}
              </Button>
            )}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No profile document yet. Upload the TSDF profile sheet, or generate an Encamp summary
            from the characterization above.
          </Typography>
        )}

        {uploadingFileName != null && (
          <UploadingLine fileName={uploadingFileName} attaching={attaching} />
        )}

        <Dropzone
          label={document ? "Replace the profile document" : "Upload the TSDF profile sheet"}
          dragActiveText="Drop the profile sheet here"
          multiple={false}
          disabled={busy || isReadOnly}
          onDrop={onDrop}
        />
      </Stack>

      {autoFillOpen && document && (
        <AutoFillProfileDialog
          open={autoFillOpen}
          onClose={() => setAutoFillOpen(false)}
          wasteStream={wasteStream}
          documentId={document.id}
          documentTitle={document.title ?? "this document"}
        />
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          if (!deleting) setPendingDelete(null);
        }}
        onConfirm={runDelete}
        header="Delete profile document?"
        resources={[{ resourceType: "Document", resourceName: pendingDelete?.title ?? "" }]}
        info="Delete this document from the waste stream? Any profile values already applied from this document will stay."
        confirmText="Delete document"
        cancelText="Cancel"
        loading={deleting}
        disableConfirm={busy || !canDeleteDocument}
      />

      <ConfirmDialog
        open={confirmReplaceOpen}
        onClose={() => setConfirmReplaceOpen(false)}
        onConfirm={runGenerate}
        header="Replace the current profile document?"
        info="This waste stream has one profile document slot. Generating an Encamp summary replaces what is there now."
        resources={[{ resourceType: "Current document", resourceName: document?.title ?? "" }]}
        confirmText="Yes, generate summary"
        cancelText="Cancel"
        loading={generating}
      />
    </ProfileSection>
  );
};
